import React, {
  ComponentProps,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  ActivityIndicator,
  Platform,
  StyleSheet,
  TextInput,
  TouchableOpacity,
} from 'react-native';

import { Box } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { IconSizes } from '@components/shared/Icon/RestyleIcon';
import { WebIcon } from '@components/shared/Icon/WebIcon';
import useGlobalSearch from '@hooks/useGlobalSearch';
import theme from '@themes/theme';

type SearchInputProps = {
  onTextChanged: (searchText: string) => void;
  value?: string;
  onEscPress?: () => void;
  onTabOut?: () => void;
  onCancel?: () => void;
  onViewAll?: () => void;
  isChatRelatedTagSearch?: boolean;
  isTagSearch?: boolean;
  placeHolder?: string;
  height?: number;
  isSearchingChatMember?: boolean;
  closeIconSize?: IconSizes;
  loading?: boolean;
  // TA-10950 #22: opt-in standard search-box spec (white bg, Grey02 default
  // border, black focus border, 48px height, hover on the X clear icon) -
  // defaults to the existing grey01/borderless look so other consumers
  // (Global Search, member/project/skill/dependency selects) are unaffected.
  searchInputVariant?: 'default' | 'standard';
} & Pick<
  ComponentProps<typeof TextInput>,
  'onFocus' | 'onBlur' | 'autoFocus' | 'editable'
>;

const SearchInput = React.forwardRef<TextInput, SearchInputProps>(
  (
    {
      onTextChanged,
      autoFocus,
      onFocus,
      onBlur,
      onEscPress,
      onTabOut,
      value,
      editable,
      isChatRelatedTagSearch,
      isTagSearch,
      onCancel,
      placeHolder,
      height = 0,
      isSearchingChatMember,
      closeIconSize = 'l',
      loading = false,
      searchInputVariant = 'default',
    },
    ref
  ) => {
    const [focused, setFocused] = useState(false);
    const [internalValue, setInternalValue] = useState('');
    const { isSearchFocus, isViewAllPress, setViewAllEvent } =
      useGlobalSearch();
    const PLACEHOLDER = isTagSearch ? 'Search Tag' : 'Search';
    const ref_inputBox = useRef<TextInput>(null);
    useImperativeHandle(ref, () => ref_inputBox.current as TextInput);
    const isStandard = searchInputVariant === 'standard';
    const standardHeight = isStandard ? 48 : 0;
    const effectiveHeight = height > 0 ? height : standardHeight;
    const standardBorderColor = focused ? 'black' : 'grey02';

    const handleClear = () => {
      ref_inputBox?.current?.focus();
      setFocused(true);
      onTextChanged('');
      setInternalValue('');
      if (onCancel) {
        setViewAllEvent(false);
        onCancel();
      }
    };

    useEffect(() => {
      (isSearchFocus || autoFocus) && ref_inputBox?.current?.focus();
    }, [isSearchFocus, autoFocus]);

    return (
      <Box flex={1} flexDirection='row' alignItems='center'>
        {Platform.OS !== 'web' && !isViewAllPress && (
          <Icon
            name='ChevronLeft'
            variant='xl'
            onPress={() => setViewAllEvent(true)}
          />
        )}
        <Box
          flex={1}
          backgroundColor={isStandard ? 'white' : 'grey01'}
          borderRadius='xs'
          borderWidth={isStandard ? 1 : undefined}
          borderColor={isStandard ? standardBorderColor : undefined}
          flexDirection='row'
          alignItems='center'
          paddingVertical={isChatRelatedTagSearch ? 'xs' : 'xxs'}
          paddingHorizontal={isChatRelatedTagSearch ? 'm' : 'xs'}
          {...(effectiveHeight > 0 ? { height: effectiveHeight } : {})}
          accessibilityLabel='Search'>
          {isSearchingChatMember && (
            <Icon
              name='Users'
              variant='s'
              borderRadius='xxs'
              padding='xxs'
              color='white'
              backgroundColor='greenSecondary'
            />
          )}
          {!isSearchingChatMember && (
            <Icon
              name={isTagSearch ? 'HashSearch' : 'Search'}
              variant='l'
              color={focused ? 'textPrimary' : 'textSecondary'}
            />
          )}
          <TextInput
            editable={editable}
            ref={ref_inputBox}
            placeholderTextColor={theme.colors.grey04}
            style={[styles.input, theme.textVariants.bodySecondary]}
            placeholder={placeHolder ?? PLACEHOLDER}
            value={Platform.OS === 'web' ? (value ?? internalValue) : value}
            autoCapitalize='none'
            autoFocus={autoFocus || isSearchFocus}
            onKeyPress={({ nativeEvent: { key } }) => {
              key === 'Tab' && focused && onTabOut?.();
              key === 'Escape' && focused && onEscPress?.();
            }}
            onBlur={(e) => {
              onBlur?.(e);
              setFocused(false);
            }}
            onFocus={(e) => {
              setTimeout(() => {
                onFocus?.(e);
                setFocused(true);
              }, 500);
            }}
            onChangeText={(str) => {
              onTextChanged(str);
            }}
          />
          {isStandard && !loading && (!!internalValue || !!value) ? (
            <WebIcon
              name='X'
              variant={closeIconSize}
              color='textPrimary'
              accessibilityLabel='Cancel'
              onPress={handleClear}
            />
          ) : (
            <TouchableOpacity
              accessibilityLabel={
                loading || (!internalValue && !value) ? undefined : 'Cancel'
              }
              accessible={!loading && (!!internalValue || !!value)}
              importantForAccessibility={
                loading || (!internalValue && !value) ? 'no' : 'yes'
              }
              disabled={loading || (!internalValue && !value)}
              onPress={handleClear}>
              {(!!internalValue || !!value) &&
                (loading ? (
                  <ActivityIndicator
                    size='small'
                    color={theme.colors.textPrimary}
                  />
                ) : (
                  <Icon name='X' variant={closeIconSize} color='textPrimary' />
                ))}
            </TouchableOpacity>
          )}
        </Box>
      </Box>
    );
  }
);
const styles = StyleSheet.create({
  input: {
    marginLeft: theme.spacing.xs,
    flex: 1,
    ...Platform.select({ web: { outlineStyle: 'none' } }),
  },
});

export default SearchInput;
